import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';
import { ResetExploreUsageButton } from './ResetExploreUsageButton';
import type { AuthMetadata, PushDeviceState } from '../../services/firebase';
import {
  cloudLoggingUrl,
  getRecentAnalyticsEvents,
  getUserActivitySummary,
  type RecentEventsResult,
  type UserActivitySummary,
} from '../../services/userInfo';
import type { SubscriptionBadge } from './subscriptionState';
import { platformLabel, type PushBadge } from './pushDeviceState';
import {
  formatEngagementSpan,
  permissionLabel,
  summariseChannels,
  toDateOrNull,
} from './userDiagnostics';
import type { AccountStatus, FirestoreUser } from '../../types';

/**
 * Users → User Info: the read-only page for one user.
 *
 * Everything display-only lives here — diagnostics, devices, subscription
 * detail, activity counts, recent analytics events — so the Edit modal holds
 * only what Save writes. The admin has no URL routes, so this renders in place
 * of the list inside UserManagement, which keeps the list's filters and page
 * intact for the Back button.
 *
 * The account actions (suspend, close, purge, grant credits…) are buttons here
 * too, but their confirm modals still belong to UserManagement: these props
 * only open them.
 */

const STATUS_LABEL: Record<AccountStatus, { label: string; variant: 'success' | 'danger' | 'warning' | 'default' }> = {
  active: { label: 'Active', variant: 'success' },
  disabled: { label: 'Disabled', variant: 'danger' },
  pendingDeletion: { label: 'Pending deletion', variant: 'warning' },
  deleted: { label: 'Deleted', variant: 'default' },
};

const PLAN_LABEL: Record<string, string> = {
  free: 'Free',
  collector: 'Collector',
  curator: 'Curator',
  horologist: 'Horologist',
};

const BOOKMARK_TYPE_LABEL: Record<string, string> = {
  brand: 'Brands',
  manufacturer: 'Manufacturers',
  reference: 'References',
  calibre: 'Calibres',
};

/** A date and its age, or null when there is no date. */
function when(value: unknown): string | null {
  const d = toDateOrNull(value);
  if (!d) return null;
  return `${d.toLocaleString()} · ${formatDistanceToNow(d, { addSuffix: true })}`;
}

function whenIso(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : when(d);
}

type Row = [string, ReactNode];

/** Rows with no value are dropped, so an empty field never renders as a dash wall. */
function Rows({ rows, empty }: { rows: Array<[string, ReactNode | null | undefined | false]>; empty?: string }) {
  const shown = rows.filter((r): r is Row => r[1] !== null && r[1] !== undefined && r[1] !== false && r[1] !== '');
  if (shown.length === 0) return <p className="text-sm text-gray-500">{empty ?? 'Nothing recorded.'}</p>;
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
      {shown.map(([label, value]) => (
        <Fragment key={label}>
          <dt className="text-gray-500">{label}</dt>
          <dd className="text-gray-900 break-words">{value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

function Card({ title, children, className = '' }: { title: string; children: ReactNode; className?: string }) {
  return (
    <section className={`bg-white shadow rounded-lg p-4 space-y-3 ${className}`}>
      <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">{title}</h2>
      {children}
    </section>
  );
}

function formatParams(params: Record<string, unknown>): string {
  return Object.entries(params)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join(' · ');
}

export interface UserInfoActions {
  onEdit: () => void;
  onToggleSuspend?: () => void;
  onCloseOnBehalf?: () => void;
  onCancelDeletion?: () => void;
  onPurge?: () => void;
  onGrantCredits?: () => void;
  onRemoveTombstone?: () => void;
}

interface UserInfoScreenProps {
  user: FirestoreUser;
  status: AccountStatus;
  isSelf: boolean;
  meta?: AuthMetadata;
  subscriptionBadge?: SubscriptionBadge | null;
  /** Undefined until the listing's push fetch resolves. */
  pushBadge?: PushBadge;
  pushDevices?: PushDeviceState;
  onBack: () => void;
  actions: UserInfoActions;
}

export function UserInfoScreen({
  user,
  status,
  isSelf,
  meta,
  subscriptionBadge,
  pushBadge,
  pushDevices,
  onBack,
  actions,
}: UserInfoScreenProps) {
  const [activity, setActivity] = useState<UserActivitySummary | null>(null);
  const [events, setEvents] = useState<RecentEventsResult | null>(null);
  const [eventsError, setEventsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setActivity(null);
    setEvents(null);
    setEventsError(null);
    getUserActivitySummary(user.uid)
      .then((a) => !cancelled && setActivity(a))
      .catch((err) => console.error('Failed to load user activity:', err));
    getRecentAnalyticsEvents(user.uid)
      .then((r) => !cancelled && setEvents(r))
      .catch((err) => {
        console.error('Failed to load analytics events:', err);
        if (!cancelled) setEventsError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [user.uid]);

  const sub = user.subscription;
  const statusInfo = STATUS_LABEL[status];
  const channels = summariseChannels(user.notificationChannelsEnabled);
  const deleted = status === 'deleted';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1 min-w-0">
          <button type="button" onClick={onBack} className="text-sm text-brand-600 hover:text-brand-700">
            ← Back to users
          </button>
          <h1 className="text-2xl font-bold text-gray-900 break-all">
            {user.email || 'No email'}
            {isSelf && <span className="ml-2 text-sm font-normal text-blue-600">(You)</span>}
          </h1>
          <p className="text-sm text-gray-500">{user.displayName || 'No display name'}</p>
          <p className="text-xs text-gray-400 font-mono">{user.uid}</p>
          <div className="flex flex-wrap gap-1 pt-1">
            <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
            <Badge variant="info">{PLAN_LABEL[user.plan] ?? user.plan}</Badge>
            {subscriptionBadge && <Badge variant={subscriptionBadge.variant}>{subscriptionBadge.label}</Badge>}
            {user.isAdmin && <Badge variant="warning">Admin</Badge>}
            {user.isContributor && <Badge variant="info">Contributor</Badge>}
            {user.submissionDisabled && <Badge variant="danger">Submissions off</Badge>}
            {user.isTestUser === true && <Badge variant="default">TEST</Badge>}
          </div>
        </div>
        {!deleted && <Button onClick={actions.onEdit}>Edit</Button>}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Account">
          <Rows
            rows={[
              ['Signed up', whenIso(meta?.creationTime) ?? when(user.createdAt)],
              ['Last sign-in', whenIso(meta?.lastSignInTime) ?? when(user.lastLoginAt)],
              ['Last token refresh', whenIso(meta?.lastRefreshTime)],
              ['Email verified', meta ? (meta.emailVerified ? 'Yes' : 'No — cannot accept a Brand Portal invite') : null],
              ['Sign-in providers', meta?.providerIds?.length ? meta.providerIds.join(', ') : null],
              ['Auth account', meta?.authDisabled ? 'Disabled in Firebase Auth' : null],
              ['Deletion requested', when(user.deletionRequestedAt)],
              ['Deletion scheduled', when(user.deletionScheduledAt)],
              [
                'Submission guidelines',
                user.submissionGuidelinesAcceptedAt
                  ? `v${user.submissionGuidelinesAcceptedVersion ?? '?'} accepted ${when(user.submissionGuidelinesAcceptedAt)}`
                  : null,
              ],
              [
                'Brand memberships',
                user.brandMemberships?.length
                  ? user.brandMemberships.map((m) => `${m.brandId} (${m.role === 'brandAdmin' ? 'admin' : 'member'})`).join(', ')
                  : null,
              ],
              [
                'AI usage',
                user.aiUsage?.count != null
                  ? `${user.aiUsage.count} on ${user.aiUsage.day ?? 'unknown day'}`
                  : null,
              ],
              ['AI cap override', user.aiUsage?.capOverride != null ? String(user.aiUsage.capOverride) : null],
              ['AI limiter', user.aiRateLimitMode ?? null],
            ]}
          />
        </Card>

        <Card title="Subscription">
          {subscriptionBadge?.title && <p className="text-sm text-gray-600">{subscriptionBadge.title}</p>}
          <Rows
            empty="No RevenueCat history — this user has never started a purchase or trial."
            rows={[
              ['State', sub?.state ?? null],
              ['Product', sub?.productId ?? null],
              ['Store', sub?.store ?? null],
              ['Environment', sub?.environment ?? null],
              ['Period', sub?.periodType ?? null],
              ['Paid monthly since', when(sub?.monthlyPaidSince)],
              ['Expires', when(sub?.expiresAt)],
              ['Cancelled', sub?.cancelledAt ? `${when(sub.cancelledAt)}${sub.cancelReason ? ` · ${sub.cancelReason}` : ''}` : null],
              ['Billing issue', sub?.billingIssue ? when(sub.billingIssueAt) ?? 'Yes' : null],
              [
                'Trial',
                user.trialTier
                  ? `${PLAN_LABEL[user.trialTier] ?? user.trialTier}${
                      user.trialStartedAt ? ` · started ${when(user.trialStartedAt)}` : ''
                    }${user.trialEndsAt ? ` · ends ${when(user.trialEndsAt)}` : ''}`
                  : null,
              ],
              ['Has ever paid', user.hasEverBeenPaid ? 'Yes' : null],
              ['AI add-on', user.hasAiAddon ? 'Yes' : null],
              [
                'Last store event',
                sub?.lastEventType || sub?.lastEventId ? (
                  <span>
                    {sub.lastEventType}
                    {sub.lastEventId && (
                      <span className="ml-1 font-mono text-xs text-gray-500" title="RevenueCat event id — the receipt reference">
                        {sub.lastEventId}
                      </span>
                    )}
                  </span>
                ) : null,
              ],
            ]}
          />
        </Card>

        <Card title="Device signals">
          <Rows
            empty="No launch diagnostics reported yet — this user has not opened a build that records them."
            rows={[
              ['App version', user.lastAppVersion ? `${user.lastAppVersion}${user.lastAppVersionAt ? ` · reported ${when(user.lastAppVersionAt)}` : ''}` : null],
              [
                'Installed',
                (() => {
                  const installed = toDateOrNull(user.installedAt);
                  const span = formatEngagementSpan(user.installedAt);
                  return installed && span ? `${installed.toLocaleDateString()} · ${span} ago` : null;
                })(),
              ],
              // `!= null`: 0 launches is a measurement, not an absence.
              ['Launches', user.launchCount != null ? String(user.launchCount) : null],
              ['Last opened', when(user.lastAppOpenedAt)],
              [
                'Notifications',
                user.notificationPermission != null
                  ? `${permissionLabel(user.notificationPermission)}${
                      toDateOrNull(user.notificationPermissionAt)
                        ? ` · recorded ${toDateOrNull(user.notificationPermissionAt)!.toLocaleDateString()}`
                        : ''
                    }`
                  : null,
              ],
              // Android only; absent on iOS by design.
              [
                'Channels',
                channels ? (
                  <span>
                    {channels.enabled} of {channels.total} on
                    {channels.disabled.length > 0 && (
                      <span className="text-amber-700"> — off: {channels.disabled.join(', ')}</span>
                    )}
                  </span>
                ) : null,
              ],
              ['Device', [user.deviceModel, user.osVersion].filter(Boolean).join(' · ') || null],
              ['Region', [user.locale, user.timezone].filter(Boolean).join(' · ') || null],
            ]}
          />
          <div>
            <h3 className="text-xs font-medium text-gray-500 uppercase mb-1">Push devices</h3>
            {!pushBadge ? (
              <p className="text-sm text-gray-500">Loading device state…</p>
            ) : pushBadge.tokenCount === 0 ? (
              <p className="text-sm text-gray-500">{pushBadge.title}</p>
            ) : (
              <ul className="space-y-1">
                {(pushDevices?.devices ?? []).map((d, i) => (
                  <li key={i} className="text-sm text-gray-700 flex flex-wrap gap-x-3">
                    <span className="font-medium">
                      {d.deviceModel || (d.platform ? platformLabel(d.platform) : 'Unknown device')}
                    </span>
                    {d.osVersion && (
                      <span className="text-gray-500">
                        {d.platform ? platformLabel(d.platform) : ''} {d.osVersion}
                      </span>
                    )}
                    {d.appVersion && <span className="text-gray-500">app {d.appVersion}</span>}
                    {d.updatedAt && (
                      <span className="text-gray-400">seen {new Date(d.updatedAt).toLocaleDateString()}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        <Card title="Activity">
          {!activity ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (
            <Rows
              rows={[
                ['Watches', activity.watches ?? 'Could not be read'],
                [
                  'Favourites',
                  activity.bookmarksByType === null
                    ? 'Could not be read'
                    : Object.keys(activity.bookmarksByType).length === 0
                      ? 'None'
                      : Object.entries(activity.bookmarksByType)
                          .map(([type, n]) => `${BOOKMARK_TYPE_LABEL[type] ?? type}: ${n}`)
                          .join(' · '),
                ],
                ['Favourite brands', activity.bookmarkedBrands.length ? activity.bookmarkedBrands.join(', ') : null],
                ['Articles authored', activity.articlesAuthored ?? 'Could not be read'],
              ]}
            />
          )}
          <p className="text-xs text-gray-400">
            Owned watches are encrypted, so their brands cannot be counted here — favourite brands come from
            bookmarks.
          </p>
        </Card>
      </div>

      <Card title="Recent events">
        {eventsError ? (
          <p className="text-sm text-red-600">Could not load events: {eventsError}</p>
        ) : !events ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <>
            {!events.ordered && (
              <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                The <code>analyticsEvents (uid, createdAt desc)</code> index is missing, so this is the newest of an
                unordered sample, not guaranteed to be the latest.
                {events.indexUrl && (
                  <>
                    {' '}
                    <a href={events.indexUrl} target="_blank" rel="noreferrer" className="underline">
                      Create the index
                    </a>
                  </>
                )}
              </p>
            )}
            {events.events.length === 0 ? (
              <p className="text-sm text-gray-500">No events recorded for this user.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase">
                      <th className="py-1 pr-4 font-medium">When</th>
                      <th className="py-1 pr-4 font-medium">Event</th>
                      <th className="py-1 font-medium">Params</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {events.events.map((e) => (
                      <tr key={e.id}>
                        <td className="py-1 pr-4 whitespace-nowrap text-gray-500" title={e.createdAt?.toLocaleString()}>
                          {e.createdAt ? formatDistanceToNow(e.createdAt, { addSuffix: true }) : '—'}
                        </td>
                        <td className="py-1 pr-4 font-mono text-gray-900 whitespace-nowrap">
                          {e.event}
                          {e.source === 'server' && <span className="ml-1 text-xs text-gray-400">(server)</span>}
                        </td>
                        <td className="py-1 text-gray-600 break-all">{formatParams(e.params)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Card>

      <Card title="Errors">
        <p className="text-sm text-gray-600">
          No per-user error or failed-call log is stored — Cloud Function errors go to Cloud Logging only.{' '}
          <a href={cloudLoggingUrl(user.uid)} target="_blank" rel="noreferrer" className="text-brand-600 underline">
            Open errors mentioning this uid (last 30 days)
          </a>
        </p>
      </Card>

      <Card title="Actions">
        <div className="flex flex-wrap gap-2">
          {deleted ? (
            actions.onRemoveTombstone && (
              <Button variant="ghost" size="sm" onClick={actions.onRemoveTombstone} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                Remove Tombstone
              </Button>
            )
          ) : (
            <>
              {actions.onGrantCredits && (
                <Button variant="ghost" size="sm" onClick={actions.onGrantCredits} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                  Grant AI Credits
                </Button>
              )}
              {actions.onCancelDeletion && (
                <Button variant="ghost" size="sm" onClick={actions.onCancelDeletion} className="text-green-600 hover:text-green-700 hover:bg-green-50">
                  Cancel Deletion
                </Button>
              )}
              {actions.onToggleSuspend && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={actions.onToggleSuspend}
                  className={
                    status === 'disabled'
                      ? 'text-green-600 hover:text-green-700 hover:bg-green-50'
                      : 'text-amber-600 hover:text-amber-700 hover:bg-amber-50'
                  }
                >
                  {status === 'disabled' ? 'Restore' : 'Suspend'}
                </Button>
              )}
              {actions.onCloseOnBehalf && (
                <Button variant="ghost" size="sm" onClick={actions.onCloseOnBehalf} className="text-orange-600 hover:text-orange-700 hover:bg-orange-50">
                  Close on behalf
                </Button>
              )}
              {actions.onPurge && (
                <Button variant="ghost" size="sm" onClick={actions.onPurge} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                  Purge Now
                </Button>
              )}
            </>
          )}
        </div>
        {/* Device-facing and immediate on confirm — an action, not a saved field. */}
        {!deleted && <ResetExploreUsageButton uid={user.uid} email={user.email} />}
      </Card>
    </div>
  );
}
